import cx from 'classnames';
import { t } from 'i18next';
import { Trans } from 'react-i18next';
import { FC, MouseEvent, MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Col } from '../grid/Col';
import { IRowProps, Row } from '../grid/Row';
import { Link } from '../link';
import PatronLevel from '../patreon/patronLevel';
import { Stack } from '../stack/Stack';
import { useTrackedImpression } from '../../scripts/hooks/useTrackedImpression';
import { XsollaCheckout } from './xsollaCheckout';
import {
  IXsollaDisplayPrice,
  IXsollaPricingResponse,
  TXsollaCheckoutMethod,
  TXsollaSubscriptionOperation,
  XSOLLA_TIERS
} from '../../../common/xsolla';
import { consumePendingCheckoutSelection } from './pendingCheckoutSelection';
import { IUnifiedSubscription } from '../../../common/purchases';
import { TXsollaSubscriptionTier } from '../../../common/xsollaConfig';
import { IXsollaCheckoutError } from '../../../common/xsollaCheckoutError';
import { buildXsollaFallbackPricing } from '../../../common/xsollaDisplayPricing';
import { TXsollaCheckoutExperience } from '../../../common/xsollaRollout';
import {
  TCreateXsollaSubscriptionToken,
  TXsollaCheckoutSelection,
  useXsollaCheckoutSession
} from './useXsollaCheckoutSession';
import { IXsollaWidgetPaymentInfo } from './xsollaWidgetAdapter';
import { buildXsollaSubscriptionOptionsViewModel, TXsollaTierCtaViewModel } from './xsollaCurrentPlanViewModel';
import { MOST_POPULAR_TIER, getPremiumTierPerks, resolvePerkValues, TPremiumTierPerk } from './premiumTierPerks';
import { TIER_TEXT_CLASS } from './xsollaTierStyles';
import {
  buildPremiumAnalyticsItem,
  getBillingPeriod,
  getCheckoutStageFromWidgetEvent,
  getPaymentMethodFamily,
  TPremiumCheckoutSurface,
  TPremiumInteractionTarget,
  TPremiumRailPosition,
  trackPremiumAddPaymentInfo,
  trackPremiumBeginCheckout,
  trackPremiumBillingPeriodSelect,
  trackPremiumCheckoutClosed,
  trackPremiumCheckoutError,
  trackPremiumCheckoutWidgetOpen,
  trackPremiumCheckoutWidgetStage,
  trackPremiumPlanChangeIntent,
  trackPremiumPlanImpression,
  trackPremiumPlanSelect
} from './premiumXsollaAnalytics';

/**
 * API function type for creating payment tokens
 */
export type TCreatePaymentTokenFn = TCreateXsollaSubscriptionToken;

export type TXsollaStartCheckoutFn = (
  tier: TXsollaSubscriptionTier,
  isAnnual: boolean,
  operation?: TXsollaSubscriptionOperation
) => void | Promise<void>;

type TXsollaSubscriptionOptionsProps = {
  className?: string;
  rowProps?: IRowProps;
  showCheckoutFooter?: boolean;
  checkoutConfig: TXsollaCheckoutExperience;
  pricing?: IXsollaPricingResponse['plans'] | null;
  isPricingLoading?: boolean;
  isPricingUnavailable?: boolean;
  isInitializing?: boolean;
  isAuthenticated?: boolean;
  disableCheckout?: boolean;
  subscriptions?: IUnifiedSubscription[];
  manageSubscriptionPath?: string | null;
  checkoutSurface?: TPremiumCheckoutSurface;
  /**
   * Distinguishes the two identical /premium rails (top vs bottom) in plan
   * impression / select / begin_checkout events. Optional: unset on the
   * settings surfaces where there is a single rail.
   */
  railPosition?: TPremiumRailPosition;
  getCheckoutSuccessUrl?: (
    tier: TXsollaSubscriptionTier,
    isAnnual: boolean,
    operation?: TXsollaCheckoutSelection['operation']
  ) => string;
  /**
   * Function to create a payment token (injected from game-specific code)
   */
  createPaymentToken: TCreatePaymentTokenFn;
  /**
   * Called when subscription is successful
   */
  onSuccess?: (paymentInfo: IXsollaWidgetPaymentInfo, selection: TXsollaCheckoutSelection) => void;
  /**
   * Called when an error occurs
   */
  onError?: (error: IXsollaCheckoutError) => void;
  /**
   * Called when checkout starts.
   */
  onCheckoutStart?: (selection: TXsollaCheckoutSelection) => void;
  /**
   * Called when the checkout reports payment is still processing.
   */
  onPending?: (paymentInfo: IXsollaWidgetPaymentInfo, selection: TXsollaCheckoutSelection) => void;
  /**
   * Called when the checkout is closed before a success/error resolution.
   * Receives the abandoned selection so callers can offer a resume action.
   */
  onClose?: (closedSelection?: TXsollaCheckoutSelection) => void;
  onRequireAuthentication?: () => void;
  /**
   * Receives this rail's startCheckout so the parent can restart an
   * interrupted checkout (e.g. from a "Resume checkout" notice action).
   */
  checkoutStarterRef?: MutableRefObject<TXsollaStartCheckoutFn | null>;
};

const CN_CHECKOUT_METHOD_META: Record<TXsollaCheckoutMethod, { icon: string; labelKey: string }> = {
  alipay: {
    icon: 'fa-brands fa-alipay',
    labelKey: 'xsolla:payment-method-alipay'
  },
  wechat: {
    icon: 'fa-brands fa-weixin',
    labelKey: 'xsolla:payment-method-wechat'
  }
};

type TTierConfig = {
  tier: TXsollaSubscriptionTier;
  name: string;
  level: number;
  className: string;
  monthlyPrice: IXsollaDisplayPrice;
  annualPrice: IXsollaDisplayPrice;
  perks: TPremiumTierPerk[];
  showMostPopular?: boolean;
};

type TSubscriptionPanelProps = {
  tierConfig: TTierConfig;
  tierViewModel: TXsollaTierCtaViewModel;
  isAnnual: boolean;
  onSubscribe: (
    tier: TXsollaSubscriptionTier,
    isAnnual: boolean,
    operation?: TXsollaCheckoutSelection['operation'],
    interactionTarget?: TPremiumInteractionTarget,
    changeDirection?: string | null
  ) => void;
  isLoading: boolean;
  isDisabled: boolean;
  isPricingLoading: boolean;
  isPricingUnavailable: boolean;
  isInitializing: boolean;
  displayLocale: string;
};

function resolveActionVariantClass(viewModel: TXsollaTierCtaViewModel, isFeatured: boolean): string {
  if (viewModel.isCurrentPlan && !viewModel.checkoutOperation && !viewModel.actionHref) {
    return 'slds-button--current-plan';
  }
  if (viewModel.isCheckoutBlocked && !viewModel.actionHref) {
    return 'slds-button--neutral';
  }
  if (viewModel.actionHref) {
    return 'slds-button--brand-gold-outline';
  }
  if (viewModel.changeKind === 'upgrade') {
    return 'slds-button--brand-gold';
  }
  if (viewModel.changeKind === 'downgrade') {
    return 'slds-button--ghost-light';
  }
  if (viewModel.changeKind === 'switch_billing') {
    return 'slds-button--brand-gold-outline';
  }
  // Only the featured tier gets the filled-gold buy button so the page has one
  // clearly recommended default; sibling tiers use the outline variant.
  return isFeatured ? 'slds-button--brand-gold' : 'slds-button--brand-gold-outline';
}

// Intl.NumberFormat construction dominates formatting cost, so formatters are
// cached per locale/currency/precision across renders.
const priceFormatterCache = new Map<string, Intl.NumberFormat>();

function formatDisplayPrice(price: IXsollaDisplayPrice, localeCode: string): string {
  // Fractional amounts keep the currency's own minor-unit default so
  // 0-decimal currencies (JPY, KRW) never show nonexistent sub-units.
  const wholeUnits = Number.isInteger(price.amount);
  const cacheKey = `${localeCode}|${price.currency}|${wholeUnits}`;

  try {
    let formatter = priceFormatterCache.get(cacheKey);
    if (!formatter) {
      formatter = new Intl.NumberFormat(localeCode, {
        style: 'currency',
        currency: price.currency,
        ...(wholeUnits ? { maximumFractionDigits: 0 } : {})
      });
      priceFormatterCache.set(cacheKey, formatter);
    }
    return formatter.format(price.amount);
  } catch {
    return `${price.currency} ${price.amount}`;
  }
}


function buildCheckoutActionLabel(
  tierViewModel: TXsollaTierCtaViewModel,
  tierName: string,
  isAnnual: boolean
): string | null {
  if (tierViewModel.actionHref || tierViewModel.isCheckoutBlocked) {
    return null;
  }

  if (tierViewModel.changeKind === 'upgrade') {
    return t('xsolla:upgrade-to-tier', { tierName });
  }
  if (tierViewModel.changeKind === 'downgrade') {
    return t('xsolla:downgrade-to-tier', { tierName });
  }
  if (tierViewModel.changeKind === 'switch_billing') {
    return t(isAnnual ? 'xsolla:switch-to-yearly' : 'xsolla:switch-to-monthly');
  }
  return t('xsolla:get-tier', { tierName });
}

const SubscriptionPanel: FC<TSubscriptionPanelProps> = ({
  tierConfig,
  tierViewModel,
  isAnnual,
  onSubscribe,
  isLoading,
  isDisabled,
  isPricingLoading,
  isPricingUnavailable,
  isInitializing,
  displayLocale
}) => {
  const isBusy = isDisabled || isPricingLoading || isInitializing;
  // The featured treatment recommends a purchase, so it yields when this card
  // is the user's current plan or would be a downgrade for them.
  const isFeatured =
    !!tierConfig.showMostPopular && !tierViewModel.isCurrentPlan && tierViewModel.changeKind !== 'downgrade';
  // The annual upsell hint only makes sense on a fresh purchase CTA.
  const isFreshPurchase =
    !tierViewModel.isCurrentPlan &&
    !tierViewModel.changeKind &&
    !tierViewModel.isCheckoutBlocked &&
    !tierViewModel.actionHref;
  // The header always shows a per-month price; annual billing is framed as its
  // monthly equivalent with the real yearly total in the note underneath.
  const headerPrice = formatDisplayPrice(
    isAnnual
      ? { ...tierConfig.annualPrice, amount: tierConfig.annualPrice.amount / 12 }
      : tierConfig.monthlyPrice,
    displayLocale
  );
  const formattedAnnualTotal = formatDisplayPrice(tierConfig.annualPrice, displayLocale);

  const resolvedActionLabel =
    buildCheckoutActionLabel(tierViewModel, tierConfig.name, isAnnual) ||
    tierViewModel.actionLabel ||
    t('xsolla:subscribe-action');
  const isActionBlocked = isBusy || tierViewModel.isCheckoutBlocked;
  const actionVariantClass = resolveActionVariantClass(tierViewModel, isFeatured);
  const buttonLabel = isInitializing
    ? t('common:loading')
    : isPricingLoading
      ? t('xsolla:loading-price')
      : resolvedActionLabel;
  const handlePanelClick = () => {
    if (isActionBlocked || tierViewModel.actionHref) {
      return;
    }

    onSubscribe(
      tierConfig.tier,
      isAnnual,
      tierViewModel.checkoutOperation || undefined,
      'card_body',
      tierViewModel.changeKind || null
    );
  };
  const handleButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onSubscribe(
      tierConfig.tier,
      isAnnual,
      tierViewModel.checkoutOperation || undefined,
      'cta_button',
      tierViewModel.changeKind || null
    );
  };

  return (
    <PatronLevel
      patronLevel={tierConfig.level}
      className={cx('rio-patreon-plug-panel h-full rio-shadow--large', {
        'rio-pointer': !isActionBlocked,
        'rio-patreon-plug-panel--current': tierViewModel.isCurrentPlan,
        'rio-patreon-plug-panel--featured': isFeatured
      })}
      contentClassName="h-full slds-grid slds-grid--vertical"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={handlePanelClick}
    >
      {isFeatured && <div className="rio-patreon-plug-panel__ribbon">{t('patreon:most-popular')}</div>}
      <Stack
        direction="vertical"
        spacingInRems={0}
        alignItems="center"
        justifyContent="center"
        className="slds-p-vertical--x-small rio-gutter slds-text-align--center"
      >
        <div
          className={cx('slds-text-heading--medium text-bold rio-text--title rio-title--caps', tierConfig.className)}
        >
          {tierConfig.name}
        </div>
        <div className="rio-subscription-panel__price">
          <span className="rio-subscription-panel__price-amount">
            {isPricingLoading ? '…' : isPricingUnavailable ? '—' : headerPrice}
          </span>
          <span className="rio-subscription-panel__price-period">
            {t('xsolla:per-period', { period: t('common:terms.month') })}
          </span>
        </div>
        {isAnnual && (
          <div className="rio-subscription-panel__price-note">
            {isPricingLoading || isPricingUnavailable
              ? ' '
              : t('xsolla:billed-yearly-at', { price: formattedAnnualTotal })}
          </div>
        )}
      </Stack>
      <div className="slds-p-around--small slds-grid slds-grid--vertical slds-grow">
        <div className="slds-m-bottom--small">
          {tierConfig.perks.map((perk) => (
            <Row className="rio-patreon-plug-perk" verticalAlign="center" align="start" key={perk.key}>
              {perk.icon && (
                <Col size={1} className="slds-text-align--center slds-p-right--x-small">
                  <i className={perk.icon} />
                </Col>
              )}
              <Col size={perk.icon ? 11 : 12} className={perk.icon ? 'slds-p-left--x-small' : ''}>
                <Trans
                  i18nKey={perk.key}
                  values={resolvePerkValues(perk)}
                  components={[<span className="text-raiderio text-bold" />]}
                />
              </Col>
            </Row>
          ))}
        </div>
        {tierViewModel.actionNote && (
          <div className="slds-text-body--small text-muted slds-m-bottom--x-small">{tierViewModel.actionNote}</div>
        )}
        <div className="rio-subscription-panel__footer">
          {tierViewModel.actionHref && resolvedActionLabel ? (
            <Link
              data-testid={`xsolla-tier-${tierConfig.tier}-cta`}
              to={tierViewModel.actionHref}
              onClick={(event) => event.stopPropagation()}
              className={cx(
                'd-block slds-button text-bold slds-text-heading--medium slds-p-vertical--x-small slds-text-align--center rio-subscription-panel__cta',
                actionVariantClass
              )}
            >
              {resolvedActionLabel}
            </Link>
          ) : (
            <button
              data-testid={`xsolla-tier-${tierConfig.tier}-cta`}
              type="button"
              disabled={isActionBlocked}
              onClick={handleButtonClick}
              className={cx(
                'd-block slds-button text-bold slds-text-heading--medium slds-p-vertical--x-small rio-subscription-panel__cta',
                actionVariantClass
              )}
            >
              {isLoading ? <i className="fa fa-spinner fa-spin" /> : buttonLabel}
            </button>
          )}
          {!isAnnual && !isPricingLoading && !isPricingUnavailable && isFreshPurchase && (
            <div className="rio-subscription-panel__annual-hint">
              {t('xsolla:annual-upsell-note', { price: formattedAnnualTotal })}
            </div>
          )}
        </div>
      </div>
    </PatronLevel>
  );
};

export const XsollaSubscriptionOptions: FC<TXsollaSubscriptionOptionsProps> = ({
  className,
  rowProps,
  showCheckoutFooter = true,
  checkoutConfig,
  pricing,
  isPricingLoading = false,
  isPricingUnavailable = false,
  isInitializing = false,
  isAuthenticated = true,
  disableCheckout = false,
  subscriptions = [],
  manageSubscriptionPath = null,
  checkoutSurface = 'subscription_options',
  railPosition,
  getCheckoutSuccessUrl,
  createPaymentToken,
  onSuccess,
  onError,
  onCheckoutStart,
  onPending,
  onClose,
  onRequireAuthentication,
  checkoutStarterRef
}) => {
  const [isAnnual, setIsAnnual] = useState(false);
  const paymentInfoTrackedRef = useRef(false);
  const checkoutMethods = checkoutConfig.availablePaymentMethods;
  const fallbackPricing: IXsollaPricingResponse['plans'] = useMemo(() => buildXsollaFallbackPricing(), []);
  const resolvedPricing = pricing || fallbackPricing;
  const selectedBillingPeriod = isAnnual ? 'annual' : 'monthly';
  const subscriptionOptionsViewModel = useMemo(
    () =>
      buildXsollaSubscriptionOptionsViewModel({
        subscriptions,
        manageSubscriptionPath,
        selectedBillingPeriod
      }),
    [manageSubscriptionPath, selectedBillingPeriod, subscriptions]
  );
  const {
    isCheckoutActive,
    checkoutToken,
    checkoutSelection,
    startCheckout,
    handleCheckoutSuccess,
    handleCheckoutError,
    handleCheckoutPending,
    handleCheckoutClose
  } = useXsollaCheckoutSession({
    createPaymentToken,
    market: checkoutConfig.marketCountry || undefined,
    language: checkoutConfig.language,
    getReturnUrl: getCheckoutSuccessUrl,
    isAuthenticated,
    onRequireAuthentication: () => {
      trackPremiumCheckoutError({
        checkoutSurface,
        marketCountry: checkoutConfig.marketCountry,
        outcome: 'auth_required'
      });
      onRequireAuthentication?.();
    },
    onCheckoutStart: (selection) => {
      const billingPeriod = getBillingPeriod(selection.isAnnual);
      onCheckoutStart?.(selection);
      trackPremiumBeginCheckout({
        checkoutSurface,
        tier: selection.tier,
        billingPeriod,
        marketCountry: checkoutConfig.marketCountry,
        operation: selection.operation,
        railPosition,
        item: buildPremiumAnalyticsItem({
          tier: selection.tier,
          billingPeriod
        })
      });
    },
    onSuccess,
    onError: (error) => {
      trackPremiumCheckoutError({
        checkoutSurface,
        marketCountry: checkoutConfig.marketCountry,
        outcome: error.code || 'failed'
      });
      onError?.(error);
    },
    onPending,
    onClose
  });

  useEffect(() => {
    if (checkoutStarterRef) {
      checkoutStarterRef.current = startCheckout;
    }
  }, [checkoutStarterRef, startCheckout]);

  // Resume a checkout that was interrupted by the login wall (or an expired
  // session): the selection was persisted before redirecting, so once the
  // buyer is back and subscription state has settled, pick up where they left.
  const resumeAttemptedRef = useRef(false);
  useEffect(() => {
    if (resumeAttemptedRef.current || !isAuthenticated || isInitializing || disableCheckout) {
      return;
    }

    resumeAttemptedRef.current = true;
    const pendingSelection = consumePendingCheckoutSelection();
    if (!pendingSelection) {
      return;
    }

    // Subscription state may have changed during the login round-trip (plan
    // bought elsewhere, lapsed, etc.), so the checkout operation is re-derived
    // from a view model built for the PENDING billing period (the component's
    // own toggle resets to monthly on remount), and states that no longer
    // allow a checkout abort the resume silently. A current-plan tier still
    // resumes when it carries an operation (switch billing / change plan).
    const resumeViewModel = buildXsollaSubscriptionOptionsViewModel({
      subscriptions,
      manageSubscriptionPath,
      selectedBillingPeriod: pendingSelection.isAnnual ? 'annual' : 'monthly'
    });
    const tierViewModel = resumeViewModel.tierCtas[pendingSelection.tier];
    if (
      !tierViewModel ||
      tierViewModel.isCheckoutBlocked ||
      tierViewModel.actionHref ||
      (tierViewModel.isCurrentPlan && !tierViewModel.checkoutOperation)
    ) {
      return;
    }

    const rederivedOperation = tierViewModel.checkoutOperation || undefined;

    // Always reflect the buyer's billing choice so a manual retry is one click,
    // whether or not we auto-open below.
    setIsAnnual(pendingSelection.isAnnual);

    // Guard against a plan that changed during the login round-trip: only auto-open
    // the payment widget when the operation current state derives STILL matches the
    // operation the buyer intended. A mismatch (e.g. an intended fresh purchase that
    // is now a change_plan/downgrade) must NOT silently auto-open — the tier/toggle
    // is pre-selected above and the user must click explicitly.
    if (rederivedOperation !== pendingSelection.operation) {
      return;
    }

    void startCheckout(pendingSelection.tier, pendingSelection.isAnnual, rederivedOperation);
  }, [disableCheckout, isAuthenticated, isInitializing, manageSubscriptionPath, startCheckout, subscriptions]);

  // Memoized so the analytics callbacks keyed on it keep stable identities.
  const tiers: TTierConfig[] = useMemo(
    () => [
      {
        tier: 'revered',
        name: t('patreon:ranks.revered'),
        level: XSOLLA_TIERS.REVERED.ordinal,
        className: 'text-item-quality--4',
        monthlyPrice: resolvedPricing.revered.monthly,
        annualPrice: resolvedPricing.revered.annual,
        perks: getPremiumTierPerks('revered', 'wowretail'),
        showMostPopular: MOST_POPULAR_TIER === 'revered'
      },
      {
        tier: 'exalted',
        name: t('patreon:ranks.exalted'),
        level: XSOLLA_TIERS.EXALTED.ordinal,
        className: 'text-item-quality--5',
        monthlyPrice: resolvedPricing.exalted.monthly,
        annualPrice: resolvedPricing.exalted.annual,
        perks: getPremiumTierPerks('exalted', 'wowretail'),
        showMostPopular: MOST_POPULAR_TIER === 'exalted'
      },
      {
        tier: 'illustrious',
        name: t('patreon:ranks.illustrious'),
        level: XSOLLA_TIERS.ILLUSTRIOUS.ordinal,
        className: 'text-item-quality--6',
        monthlyPrice: resolvedPricing.illustrious.monthly,
        annualPrice: resolvedPricing.illustrious.annual,
        perks: getPremiumTierPerks('illustrious', 'wowretail'),
        showMostPopular: MOST_POPULAR_TIER === 'illustrious'
      }
    ],
    [resolvedPricing]
  );
  const activePlanSummary = subscriptionOptionsViewModel.activePlanSummary;
  const getTierAnalyticsItem = useCallback(
    (tierConfig: TTierConfig, annual: boolean) => {
      const price = annual ? tierConfig.annualPrice : tierConfig.monthlyPrice;

      return buildPremiumAnalyticsItem({
        tier: tierConfig.tier,
        billingPeriod: getBillingPeriod(annual),
        price: price.amount,
        currency: price.currency,
        index: tiers.findIndex((tier) => tier.tier === tierConfig.tier) + 1
      });
    },
    [tiers]
  );
  const planImpressionItems = useMemo(
    () => tiers.map((tierConfig) => getTierAnalyticsItem(tierConfig, isAnnual)),
    [getTierAnalyticsItem, isAnnual, tiers]
  );
  const planImpressionRef = useTrackedImpression<HTMLDivElement>({
    enabled: !isPricingLoading && !isPricingUnavailable && !isInitializing,
    minVisibleMs: 900,
    onImpression: () =>
      trackPremiumPlanImpression({
        checkoutSurface,
        marketCountry: checkoutConfig.marketCountry,
        items: planImpressionItems,
        railPosition,
        checkoutProvider: 'xsolla'
      })
  });
  const getCheckoutAnalyticsItem = useCallback(
    (tier: TXsollaSubscriptionTier, annual: boolean) => {
      const tierConfig = tiers.find((candidate) => candidate.tier === tier);
      return tierConfig
        ? getTierAnalyticsItem(tierConfig, annual)
        : buildPremiumAnalyticsItem({
            tier,
            billingPeriod: getBillingPeriod(annual)
          });
    },
    [getTierAnalyticsItem, tiers]
  );
  const getSelectionAnalyticsParams = useCallback(
    (selection: TXsollaCheckoutSelection | null) => {
      if (!selection) {
        return {
          checkoutSurface,
          marketCountry: checkoutConfig.marketCountry
        };
      }

      return {
        checkoutSurface,
        tier: selection.tier,
        billingPeriod: getBillingPeriod(selection.isAnnual),
        marketCountry: checkoutConfig.marketCountry,
        operation: selection.operation,
        item: getCheckoutAnalyticsItem(selection.tier, selection.isAnnual)
      };
    },
    [checkoutConfig.marketCountry, checkoutSurface, getCheckoutAnalyticsItem]
  );
  const handleBillingPeriodSelect = useCallback(
    (annual: boolean) => {
      if (annual === isAnnual) {
        return;
      }

      setIsAnnual(annual);
      trackPremiumBillingPeriodSelect({
        checkoutSurface,
        billingPeriod: getBillingPeriod(annual),
        marketCountry: checkoutConfig.marketCountry
      });
    },
    [checkoutConfig.marketCountry, checkoutSurface, isAnnual]
  );
  const handleSubscribe = useCallback(
    (
      tier: TXsollaSubscriptionTier,
      annual: boolean,
      operation?: TXsollaCheckoutSelection['operation'],
      interactionTarget: TPremiumInteractionTarget = 'cta_button',
      changeDirection?: string | null
    ) => {
      const item = getCheckoutAnalyticsItem(tier, annual);

      trackPremiumPlanSelect({
        checkoutSurface,
        tier,
        billingPeriod: getBillingPeriod(annual),
        interactionTarget,
        marketCountry: checkoutConfig.marketCountry,
        operation,
        item,
        railPosition
      });

      if (operation === 'change_plan') {
        trackPremiumPlanChangeIntent({
          checkoutSurface,
          tier,
          billingPeriod: getBillingPeriod(annual),
          changeDirection,
          marketCountry: checkoutConfig.marketCountry
        });
      }

      void startCheckout(tier, annual, operation);
    },
    [checkoutConfig.marketCountry, checkoutSurface, getCheckoutAnalyticsItem, railPosition, startCheckout]
  );

  return (
    <div className={className}>
      {/* Monthly/Yearly Toggle */}
      <div className="slds-text-align--center slds-m-bottom--medium">
        <div className="slds-p-around--xx-small rio-billing-toggle">
          <button
            data-testid="xsolla-billing-monthly"
            type="button"
            className={cx(
              'slds-text-body--small slds-p-vertical--small slds-p-horizontal--x-large rio-billing-toggle__option',
              { 'rio-billing-toggle__option--active': !isAnnual }
            )}
            onClick={() => handleBillingPeriodSelect(false)}
            disabled={disableCheckout || isInitializing}
          >
            {t('common:terms.monthly')}
          </button>
          <button
            data-testid="xsolla-billing-annual"
            type="button"
            className={cx(
              'slds-text-body--small slds-p-vertical--small slds-p-horizontal--x-large rio-billing-toggle__option',
              { 'rio-billing-toggle__option--active': isAnnual }
            )}
            onClick={() => handleBillingPeriodSelect(true)}
            disabled={disableCheckout || isInitializing}
          >
            {t('common:terms.yearly')}
            <span className="rio-billing-toggle__badge">{t('patreon:best-value')}</span>
          </button>
        </div>
      </div>

      {/* Tier Cards */}
      <div ref={planImpressionRef} style={{ width: '100%', overflow: 'hidden' }}>
        <Row
          className="rio-patreon-plug"
          gutter={{ xl: 'medium', lg: 'x-small', md: 'small', xs: 'x-small' }}
          verticalAlign={{ md: 'stretch', xs: 'center' }}
          align="center"
          {...rowProps}
        >
          {tiers.map((tierConfig, index) => (
            <Col
              lg={4}
              md={{ size: 6, order: index + 1 }}
              xs={{ size: 12, order: tierConfig.showMostPopular ? 0 : index + 1 }}
              key={tierConfig.level}
              data-testid={`xsolla-tier-${tierConfig.tier}-panel`}
            >
              <SubscriptionPanel
                tierConfig={tierConfig}
                tierViewModel={subscriptionOptionsViewModel.tierCtas[tierConfig.tier]}
                isAnnual={isAnnual}
                onSubscribe={handleSubscribe}
                isLoading={
                  isCheckoutActive &&
                  checkoutSelection?.tier === tierConfig.tier &&
                  checkoutSelection?.isAnnual === isAnnual
                }
                isDisabled={isCheckoutActive || disableCheckout}
                isPricingLoading={isPricingLoading}
                isPricingUnavailable={isPricingUnavailable}
                isInitializing={isInitializing}
                displayLocale={checkoutConfig.displayLocale}
              />
            </Col>
          ))}
        </Row>
      </div>

      {showCheckoutFooter && (
        <div className="rio-payment-methods slds-m-top--large">
          <div className="slds-text-title--caps slds-m-bottom--x-small">{t('xsolla:accepted-payment-methods')}</div>
          <div className="slds-m-bottom--small rio-payment-methods__icons">
            {checkoutMethods.length > 0 ? (
              <>
                {checkoutMethods.map((paymentMethod) => {
                  const methodMeta = CN_CHECKOUT_METHOD_META[paymentMethod];

                  return (
                    <i
                      key={paymentMethod}
                      className={`slds-text-heading--large ${methodMeta.icon}`}
                      title={t(methodMeta.labelKey)}
                    />
                  );
                })}
              </>
            ) : (
              <span className="text-muted">{t('xsolla:payment-methods-info')}</span>
            )}
          </div>
          <div className="slds-text-body--small rio-payment-methods__secure">
            <i className="fa fa-lock" />
            {t('xsolla:secure-checkout')}.
          </div>
          <div className="slds-text-body--small rio-payment-methods__secure slds-m-top--xx-small slds-m-left--small">
            <i className="fa fa-rotate-left" />
            {t('xsolla:cancel-anytime-note')}
          </div>
          {activePlanSummary && (
            <div className="slds-m-top--small">
              <div className="rio-current-subscription-pill">
                <span>
                  <Trans
                    i18nKey={
                      activePlanSummary.billingPeriodLabel
                        ? 'xsolla:current-subscription-with-billing-period'
                        : 'xsolla:current-subscription'
                    }
                    values={{
                      tierName: activePlanSummary.tierName,
                      billingPeriodLabel: activePlanSummary.billingPeriodLabel
                    }}
                    components={{
                      plan: <strong />,
                      tier: <span className={cx(activePlanSummary.tier && TIER_TEXT_CLASS[activePlanSummary.tier])} />
                    }}
                  />
                </span>
                {activePlanSummary.actionLabel && activePlanSummary.actionPath && (
                  <Link to={activePlanSummary.actionPath} className="rio-current-subscription-pill__link">
                    {activePlanSummary.actionLabel}
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Xsolla Checkout Widget */}
      {checkoutToken && (
        <XsollaCheckout
          token={checkoutToken}
          sandbox={checkoutConfig.sandbox}
          onSuccess={handleCheckoutSuccess}
          onError={handleCheckoutError}
          onPending={handleCheckoutPending}
          onClose={handleCheckoutClose}
          onWidgetOpen={() => {
            paymentInfoTrackedRef.current = false;
            trackPremiumCheckoutWidgetOpen(getSelectionAnalyticsParams(checkoutSelection));
          }}
          onWidgetStage={(event) => {
            const stage = getCheckoutStageFromWidgetEvent(event);
            const paymentInfo = event.type === 'payment_info' ? event.paymentInfo : undefined;
            const paymentMethodFamily = paymentInfo
              ? getPaymentMethodFamily(
                  String(
                    paymentInfo.payment_method || paymentInfo.payment_method_name || paymentInfo.paymentMethod || ''
                  )
                )
              : undefined;

            if (stage === 'payment_submit' && !paymentInfoTrackedRef.current) {
              paymentInfoTrackedRef.current = true;
              trackPremiumAddPaymentInfo({
                ...getSelectionAnalyticsParams(checkoutSelection),
                paymentInfo
              });
            }

            trackPremiumCheckoutWidgetStage({
              ...getSelectionAnalyticsParams(checkoutSelection),
              checkoutStage: stage,
              paymentMethodFamily
            });

            if (stage === 'error') {
              trackPremiumCheckoutError(getSelectionAnalyticsParams(checkoutSelection));
            } else if (stage === 'closed') {
              trackPremiumCheckoutClosed(getSelectionAnalyticsParams(checkoutSelection));
            }
          }}
          onPaymentInfo={(paymentInfo) => {
            if (paymentInfoTrackedRef.current) {
              return;
            }

            paymentInfoTrackedRef.current = true;
            trackPremiumAddPaymentInfo({
              ...getSelectionAnalyticsParams(checkoutSelection),
              paymentInfo
            });
          }}
        />
      )}
    </div>
  );
};

export default XsollaSubscriptionOptions;
